import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import api, { isAuthed, getUser, errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import Navbar from "@/components/Navbar";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Phone, Mail, KeyRound, DoorOpen, ClipboardList, Sparkles, ExternalLink, ShieldCheck, ShieldAlert, Ban } from "lucide-react";

function formatDate(iso, IT) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(IT ? "it-IT" : "en-US", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return iso; }
}

export default function GuestBookingDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { lang } = useLang();
  const IT = lang === "it";
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/guest/bookings/${id}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || (IT ? "Impossibile caricare la prenotazione." : "Cannot load booking.")))
      .finally(() => setLoading(false));
  }, [id, IT]);

  if (!isAuthed()) return <Navigate to="/guest/auth?mode=login" replace/>;
  if (getUser()?.role !== "guest") return <Navigate to="/dashboard" replace/>;

  if (loading) return <div className="min-h-screen grid place-items-center">{IT ? "Caricamento…" : "Loading…"}</div>;
  if (error || !data) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar/>
        <div className="max-w-2xl mx-auto px-6 py-16 text-center">
          <p className="text-sm text-destructive mb-4">{error || (IT ? "Prenotazione non trovata." : "Booking not found.")}</p>
          <Link to="/guest/dashboard" className="text-primary underline text-sm">← {IT ? "Torna alle mie prenotazioni" : "Back to my bookings"}</Link>
        </div>
      </div>
    );
  }

  const { booking: b, property: p, room: r, access_instructions: acc } = data;
  const desc = IT ? p.description_it : (p.description_en || p.description_it);
  const isSelf = acc?.type === "self";
  const cancelToken = b.session_id || b.checkout_session_id;

  const reload = () => {
    api.get(`/guest/bookings/${id}`).then((r) => setData(r.data)).catch(() => {});
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar/>
      <div className="max-w-4xl mx-auto px-6 py-10" data-testid="guest-booking-detail">
        <Link to="/guest/dashboard" className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1 mb-4">
          <ArrowLeft className="w-4 h-4"/>{IT ? "Le mie prenotazioni" : "My bookings"}
        </Link>

        {/* Hero */}
        <div className="grid md:grid-cols-[300px_1fr] gap-6 mb-8">
          {p.hero_image && (
            <div className="aspect-[4/3] rounded-lg overflow-hidden bg-muted">
              <img src={p.hero_image} alt={p.name} className="w-full h-full object-cover"/>
            </div>
          )}
          <div>
            <div className="uppercase tracking-[0.2em] text-[10px] text-sage font-semibold mb-2 flex items-center gap-1">
              <MapPin className="w-3 h-3"/>{p.city}, {p.region}
            </div>
            <h1 className="font-display text-4xl mb-2">{p.name}</h1>
            {desc && <p className="text-sm text-muted-foreground line-clamp-3 mb-3">{desc}</p>}
            <Link to={`/p/${p.slug}`} className="text-xs text-primary hover:underline inline-flex items-center gap-1">
              {IT ? "Vedi la vetrina completa" : "See full listing"} <ExternalLink className="w-3 h-3"/>
            </Link>
          </div>
        </div>

        {/* Booking summary */}
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          <SummaryCard title={IT ? "Camera & Date" : "Room & Dates"}>
            <Row label={IT ? "Camera" : "Room"} value={r.name || "—"}/>
            <Row label="Check-in" value={formatDate(b.check_in, IT)}/>
            <Row label="Check-out" value={formatDate(b.check_out, IT)}/>
            {b.nights && <Row label={IT ? "Notti" : "Nights"} value={b.nights}/>}
            {b.guests && <Row label={IT ? "Ospiti" : "Guests"} value={b.guests}/>}
          </SummaryCard>
          <SummaryCard title={IT ? "Prezzo & Stato" : "Price & Status"}>
            {b.amount != null && <Row label={IT ? "Totale" : "Total"} value={`€ ${Number(b.amount).toFixed(0)}`}/>}
            <Row label={IT ? "Pagamento" : "Payment"} value={b.payment_status || "—"}/>
            {p.phone && <Row label={IT ? "Telefono host" : "Host phone"} value={<a href={`tel:${p.phone}`} className="text-primary hover:underline">{p.phone}</a>}/>}
            {p.email && <Row label={IT ? "Email host" : "Host email"} value={<a href={`mailto:${p.email}`} className="text-primary hover:underline">{p.email}</a>}/>}
          </SummaryCard>
        </div>

        {/* Self-service cancellation — respects the free-cancel window snapshotted
            at booking time. After the window a penalty may be charged. */}
        {cancelToken && (
          <CancellationCard booking={b} sessionId={cancelToken} IT={IT} onCancelled={reload}/>
        )}

        {/* Access instructions */}
        <section className="border border-primary/30 bg-sand/30 rounded-lg p-5 mb-6" data-testid="access-instructions">
          <div className="flex items-center gap-2 mb-3">
            {isSelf ? <KeyRound className="w-5 h-5 text-primary"/> : <DoorOpen className="w-5 h-5 text-primary"/>}
            <h2 className="font-display text-2xl">{IT ? "Accesso alla struttura" : "How to check in"}</h2>
          </div>
          {isSelf ? (
            <div className="space-y-2 text-sm">
              {acc.self_code && (
                <div className="bg-background border border-border rounded-md p-3 flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{IT ? "Codice cassetta / porta" : "Lockbox / door code"}</span>
                  <span className="font-mono text-2xl font-bold text-primary tracking-widest">{acc.self_code}</span>
                </div>
              )}
              {acc.self_lockbox_location && (
                <Row label={IT ? "Posizione" : "Location"} value={acc.self_lockbox_location}/>
              )}
              {acc.notes && (
                <div className="text-sm text-foreground mt-2 whitespace-pre-wrap bg-background/60 rounded p-3 border border-border/60">
                  {acc.notes}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2 text-sm">
              <Row label={IT ? "Modalità" : "Type"} value={IT ? "Accoglienza di persona" : "In-person welcome"}/>
              {acc?.checkin_from && <Row label={IT ? "Check-in dalle" : "Check-in from"} value={acc.checkin_from}/>}
              {acc?.checkin_to && <Row label={IT ? "fino alle" : "until"} value={acc.checkin_to}/>}
              {acc?.contact_name && <Row label={IT ? "Referente" : "Host"} value={acc.contact_name}/>}
              {acc?.contact_phone && <Row label={IT ? "Telefono" : "Phone"} value={<a href={`tel:${acc.contact_phone}`} className="text-primary hover:underline">{acc.contact_phone}</a>}/>}
              {acc?.notes && (
                <div className="text-sm text-foreground mt-2 whitespace-pre-wrap bg-background/60 rounded p-3 border border-border/60">
                  {acc.notes}
                </div>
              )}
              {!acc?.checkin_from && !acc?.contact_name && !acc?.notes && (
                <p className="text-sm text-muted-foreground">
                  {IT ? "L'host ti contatterà per organizzare l'arrivo." : "The host will contact you to arrange your arrival."}
                </p>
              )}
            </div>
          )}
          {b.self_checkin_token && (
            <div className="mt-4 pt-4 border-t border-border/60">
              <a
                href={`/self-checkin/${b.self_checkin_token}`}
                target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 text-sm bg-primary text-primary-foreground px-4 py-2 rounded-full hover:bg-primary/90 lift"
                data-testid="self-checkin-link"
              >
                <ShieldCheck className="w-4 h-4"/>{IT ? "Compila i dati Alloggiati Web" : "Fill guest registration"}
              </a>
              <p className="text-[11px] text-muted-foreground mt-2">
                {IT ? "Obbligatorio per legge in Italia. Inserisci i tuoi dati e una foto del documento." : "Legally required in Italy. Enter your details and a photo of your ID."}
              </p>
            </div>
          )}
        </section>

        {/* House rules */}
        {(p.house_rules && (p.house_rules.text_it || p.house_rules.text_en || p.house_rules.checkin || p.house_rules.checkout)) && (
          <section className="border border-border rounded-lg p-5 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <ClipboardList className="w-5 h-5 text-sage"/>
              <h2 className="font-display text-2xl">{IT ? "Regole della casa" : "House rules"}</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              {p.house_rules.checkin && <Row label="Check-in" value={p.house_rules.checkin}/>}
              {p.house_rules.checkout && <Row label="Check-out" value={p.house_rules.checkout}/>}
              {p.house_rules.pets != null && <Row label={IT ? "Animali" : "Pets"} value={p.house_rules.pets ? (IT ? "Ammessi" : "Allowed") : (IT ? "Non ammessi" : "Not allowed")}/>}
              {p.house_rules.smoking != null && <Row label={IT ? "Fumo" : "Smoking"} value={p.house_rules.smoking ? (IT ? "Consentito" : "Allowed") : (IT ? "Vietato" : "Not allowed")}/>}
            </div>
            {(p.house_rules.text_it || p.house_rules.text_en) && (
              <div className="text-sm text-foreground mt-3 whitespace-pre-wrap">
                {IT ? p.house_rules.text_it : (p.house_rules.text_en || p.house_rules.text_it)}
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ title, children }) {
  return (
    <div className="border border-border rounded-lg p-5 bg-card">
      <h3 className="font-display text-lg mb-3 flex items-center gap-2"><Sparkles className="w-4 h-4 text-primary"/>{title}</h3>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}
function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm border-b border-border/40 last:border-0 pb-1.5 last:pb-0">
      <span className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</span>
      <span className="text-right">{value}</span>
    </div>
  );
}

// Self-service cancellation card — mirrors GuestPortal's FreeCancelCard, but
// scoped to the authenticated dashboard flow. Uses `b.session_id` as the
// unguessable token that gates the /bookings/{id}/cancel endpoint.
function CancellationCard({ booking, sessionId, IT, onCancelled }) {
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  const cancelled = booking?.status === "cancelled"
    || ["cancelled", "no_show_charged", "refunded", "partial_refund"].includes(booking?.payment_status);
  if (cancelled) {
    return (
      <div className="border border-destructive/40 rounded-lg p-5 bg-destructive/5 mb-6" data-testid="guest-cancelled-banner">
        <div className="flex items-center gap-2 mb-1">
          <Ban className="w-4 h-4 text-destructive"/>
          <div className="font-semibold text-destructive">{IT ? "Prenotazione cancellata" : "Booking cancelled"}</div>
        </div>
        <p className="text-xs text-muted-foreground">
          {booking?.payment_status === "refunded"
            ? (IT ? "Rimborso completo emesso sulla carta usata al pagamento." : "Full refund issued to the original card.")
            : booking?.payment_status === "partial_refund"
            ? (IT ? `Rimborso parziale di € ${Number(booking?.refund_amount || 0).toFixed(2)} emesso.` : `Partial refund of € ${Number(booking?.refund_amount || 0).toFixed(2)} issued.`)
            : (IT ? "Nessun addebito è stato effettuato." : "No charge has been made.")}
        </p>
      </div>
    );
  }

  const fc = booking?.free_cancel;
  if (!fc || !fc.deadline) {
    // No cancellation policy configured — hide the card entirely.
    return null;
  }
  const deadlineDate = new Date(fc.deadline);
  const secondsLeft = Math.max(0, Math.floor((deadlineDate.getTime() - now) / 1000));
  const days = Math.floor(secondsLeft / 86400);
  const hours = Math.floor((secondsLeft % 86400) / 3600);
  const mins = Math.floor((secondsLeft % 3600) / 60);
  const open = secondsLeft > 0;

  const cancel = async () => {
    const willCharge = !open;
    const amt = Number(fc.late_cancel_penalty || 0).toFixed(2);
    const msg = willCharge
      ? (IT
          ? `Sei fuori dalla finestra di cancellazione gratuita.\nProseguendo verrà addebitata la penale di € ${amt} sulla carta a garanzia.\n\nConfermi?`
          : `You're past the free-cancellation window.\nProceeding will charge the € ${amt} penalty to the card on file.\n\nConfirm?`)
      : (IT
          ? "Confermi la cancellazione della prenotazione? Nessun addebito verrà effettuato."
          : "Confirm cancellation? No charge will be made.");
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const r = await api.post(`/bookings/${sessionId}/cancel`);
      if (r.data?.refunded) {
        toast.success(IT
          ? `Prenotazione cancellata · Rimborso € ${Number(r.data.refund_amount || 0).toFixed(2)}`
          : `Booking cancelled · Refund € ${Number(r.data.refund_amount || 0).toFixed(2)}`);
      } else if (r.data?.charged || r.data?.penalty_amount) {
        toast.success(IT
          ? `Prenotazione cancellata · Addebito € ${Number(r.data.penalty_amount || r.data.amount || 0).toFixed(2)}`
          : `Booking cancelled · Charged € ${Number(r.data.penalty_amount || r.data.amount || 0).toFixed(2)}`);
      } else {
        toast.success(IT ? "Prenotazione cancellata" : "Booking cancelled");
      }
      onCancelled?.();
    } catch (e) {
      toast.error(errMsg(e, "Error"));
    } finally { setBusy(false); }
  };

  return (
    <div className={`border rounded-lg p-5 mb-6 ${open ? "border-sage/40 bg-sage/5" : "border-destructive/40 bg-destructive/5"}`} data-testid="guest-cancel-card">
      <div className={`uppercase tracking-[0.2em] text-[10px] font-semibold mb-2 flex items-center gap-1 ${open ? "text-sage" : "text-destructive"}`}>
        {open ? <ShieldCheck className="w-3.5 h-3.5"/> : <ShieldAlert className="w-3.5 h-3.5"/>}
        {open
          ? (IT ? "Cancellazione gratuita" : "Free cancellation")
          : (IT ? "Finestra scaduta" : "Window expired")}
      </div>
      {open ? (
        <>
          <div className="text-sm mb-1">
            {IT
              ? <>Puoi cancellare gratuitamente entro il <strong>{deadlineDate.toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}</strong></>
              : <>You can cancel free of charge by <strong>{deadlineDate.toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" })}</strong></>}
          </div>
          <div className="text-xs text-muted-foreground mb-4" data-testid="guest-cancel-countdown">
            {IT ? "Tempo rimanente:" : "Time left:"}{" "}
            <span className="font-mono text-sage font-semibold">
              {days > 0 ? `${days}g ` : ""}{String(hours).padStart(2, "0")}h {String(mins).padStart(2, "0")}m
            </span>
          </div>
          <button
            type="button" onClick={cancel} disabled={busy}
            data-testid="guest-cancel-btn"
            className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md border border-destructive/50 bg-destructive/5 text-destructive hover:bg-destructive hover:text-white transition-all lift disabled:opacity-50"
          >
            <Ban className="w-4 h-4"/>
            {busy ? (IT ? "Cancellazione…" : "Cancelling…") : (IT ? "Cancella prenotazione" : "Cancel booking")}
          </button>
        </>
      ) : (
        <>
          <div className="text-sm mb-2">
            {IT
              ? <>La finestra di cancellazione gratuita è scaduta il <strong>{deadlineDate.toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}</strong>.</>
              : <>The free-cancellation window ended on <strong>{deadlineDate.toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" })}</strong>.</>}
          </div>
          <div className="text-xs text-muted-foreground mb-4 leading-relaxed">
            {IT
              ? <>Puoi comunque cancellare, ma verrà addebitata la penale di <strong className="text-destructive">€ {Number(fc.late_cancel_penalty || 0).toFixed(2)}</strong> come da politica accettata al momento della prenotazione.</>
              : <>You can still cancel, but the penalty of <strong className="text-destructive">€ {Number(fc.late_cancel_penalty || 0).toFixed(2)}</strong> will be charged as per the policy accepted at booking time.</>}
          </div>
          <button
            type="button" onClick={cancel} disabled={busy}
            data-testid="guest-cancel-btn"
            className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md border border-destructive bg-destructive/10 text-destructive hover:bg-destructive hover:text-white transition-all lift disabled:opacity-50"
          >
            <Ban className="w-4 h-4"/>
            {busy ? (IT ? "Addebito in corso…" : "Charging…") : (IT ? `Cancella e paga € ${Number(fc.late_cancel_penalty || 0).toFixed(2)}` : `Cancel and pay € ${Number(fc.late_cancel_penalty || 0).toFixed(2)}`)}
          </button>
        </>
      )}
    </div>
  );
}
